const stripTrailingSlash = (url: string) => url.replace(/\/$/, "")

/** Admin hostname (e.g. admin.localhost, admin.dominioncity.ie). */
export const getAdminHostname = (hostname = window.location.hostname): string => {
  const fromEnv = import.meta.env.VITE_ADMIN_HOST
  if (fromEnv) return fromEnv

  const host = hostname.toLowerCase()
  if (host === "localhost" || host === "127.0.0.1") return "admin.localhost"
  if (host.startsWith("admin.")) return host

  const bare = host.replace(/^www\./, "")
  return `admin.${bare}`
}

/** Public site hostname paired with the admin subdomain. */
export const getPublicHostname = (hostname = window.location.hostname): string => {
  const fromEnv = import.meta.env.VITE_PUBLIC_HOST
  if (fromEnv) return fromEnv

  const host = hostname.toLowerCase()
  if (host === "admin.localhost") return "localhost"
  if (host.startsWith("admin.")) return host.slice("admin.".length)

  return host.replace(/^www\./, "")
}

export const isAdminHost = (hostname = window.location.hostname): boolean => {
  if (import.meta.env.VITE_FORCE_ADMIN === "true") return true

  const host = hostname.toLowerCase()
  return host === "admin.localhost" || host.startsWith("admin.")
}

export const getAdminLoginPath = () => "/login"

export const getAdminLoginUrl = () => `${getAdminOrigin()}${getAdminLoginPath()}`

export const getAdminHomePath = () => "/"

export const adminPath = (segment = "") => {
  if (!segment || segment === "/") return "/"
  const cleaned = segment.replace(/^\//, "")
  return `/${cleaned}`
}

const originForHost = (hostname: string) => {
  const { protocol, port } = window.location
  const portSuffix = port && port !== "80" && port !== "443" ? `:${port}` : ""
  return `${protocol}//${hostname}${portSuffix}`
}

/** Full origin for the admin CMS (e.g. https://admin.dominioncity.ie). */
export const getAdminOrigin = (): string => {
  const fromEnv = import.meta.env.VITE_ADMIN_SITE_URL
  if (fromEnv) return stripTrailingSlash(fromEnv)

  return originForHost(getAdminHostname())
}

/** Full origin for the public website (e.g. https://dominioncity.ie). */
export const getPublicSiteOrigin = (): string => {
  const fromEnv = import.meta.env.VITE_PUBLIC_SITE_URL
  if (fromEnv) return stripTrailingSlash(fromEnv)

  return originForHost(getPublicHostname())
}

/** Public chapter intake upload path (no CMS login required). */
export const getChapterIntakeUploadPath = () => "/chapter-intake"

/** Full URL for Step 2 image uploads after the Google Form. */
export const getChapterIntakeUploadUrl = (params?: { chapter?: string; email?: string }) => {
  const origin = getAdminOrigin()
  const path = getChapterIntakeUploadPath()
  const search = new URLSearchParams()
  if (params?.chapter) search.set("chapter", params.chapter)
  if (params?.email) search.set("email", params.email)
  const query = search.toString()
  return query ? `${origin}${path}?${query}` : `${origin}${path}`
}
